import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useWorkspace } from '@/contexts/WorkspaceContext';

export interface JobFilters {
  search?: string;
  customer_id?: string;
  supplier_id?: string;
  progress_status?: string;
  user_assigned?: string;
  limit?: number;
  offset?: number;
}

export function useParserJobs(filters?: JobFilters) {
  const { currentWorkspace } = useWorkspace();
  
  return useQuery({
    queryKey: ['parser-jobs', currentWorkspace?.id, filters],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('parser-jobs-manage', {
        body: { action: 'list_jobs', filters },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id,
  });
}

export function useParserJob(jobId: string) {
  const { currentWorkspace } = useWorkspace();
  
  return useQuery({
    queryKey: ['parser-job', currentWorkspace?.id, jobId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('parser-jobs-manage', {
        body: { action: 'get_job', job_id: jobId },
      });
      if (error) throw error;
      return data;
    },
    enabled: !!currentWorkspace?.id && !!jobId,
  });
}

export function useUpdateJob() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async ({ jobId, updates }: { jobId: string; updates: any }) => {
      const { data, error } = await supabase.functions.invoke('parser-jobs-manage', {
        body: { action: 'update_job', job_id: jobId, updates },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parser-jobs', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['parser-job'] });
      toast({ title: 'Job updated successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update job',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useBulkJobAction() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async ({
      jobIds,
      action,
    }: {
      jobIds: string[];
      action: 'manual_parser' | 'reparse' | 'mark_issue';
    }) => {
      const { data, error } = await supabase.functions.invoke('parser-jobs-manage', {
        body: { action: 'bulk_job_action', job_ids: jobIds, bulk_action_type: action },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['parser-jobs', currentWorkspace?.id] });
      toast({
        title: 'Bulk action completed',
        description: `${data.success_count} job(s) updated`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Bulk action failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useManualParseSubmit() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async ({
      jobId,
      parsedData,
      isDraft = false,
    }: {
      jobId: string;
      parsedData: any;
      isDraft?: boolean;
    }) => {
      const { data, error } = await supabase.functions.invoke('parser-jobs-manage', {
        body: {
          action: 'manual_parse_submit',
          job_id: jobId,
          parsed_data: parsedData,
          is_draft: isDraft,
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['parser-jobs', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['parser-job'] });
      queryClient.invalidateQueries({ queryKey: ['collection-list', currentWorkspace?.id] });
      toast({
        title: variables.isDraft ? 'Progress saved' : 'Parse completed',
        description: variables.isDraft
          ? 'Your work has been saved'
          : 'Bill parsed and ready for review',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to submit parse',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useApproveParsedBill() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useWorkspace();

  return useMutation({
    mutationFn: async (jobId: string) => {
      const { data, error } = await supabase.functions.invoke('parser-jobs-manage', {
        body: { action: 'approve_parsed_bill', job_id: jobId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parser-jobs', currentWorkspace?.id] });
      queryClient.invalidateQueries({ queryKey: ['collection-list', currentWorkspace?.id] });
      toast({
        title: 'Bill approved',
        description: 'Bill marked as valid and approved',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to approve bill',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
